// Modal flotante draggable para el cuaderno del alumno (1 notepad × lesson).
// No-modal: convive con el contenido de la lección debajo. Posición y estado
// open/closed se persisten en localStorage para sobrevivir a recargas y a
// navegación entre lecciones.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '@/components/atoms/Icon';
import { Spinner } from '@/components/atoms/Spinner';
import { ApiError } from '@/services/api';
import { getOrCreateNotepad, updateNotepad, type Notepad } from '@/services/notepads';
import styles from './NotepadFloatingModal.module.scss';

interface NotepadFloatingModalProps {
  lessonId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface Position {
  x: number;
  y: number;
}

const POSITION_STORAGE_KEY = 'knooowy:notepad:position';
const AUTOSAVE_DELAY_MS = 1500;

function loadPosition(): Position | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(POSITION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Position;
    if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') return parsed;
  } catch {
    /* ignore corrupt entry */
  }
  return null;
}

function clampPosition(pos: Position, panelWidth: number, panelHeight: number): Position {
  if (typeof window === 'undefined') return pos;
  const maxX = Math.max(0, window.innerWidth - panelWidth);
  const maxY = Math.max(0, window.innerHeight - panelHeight);
  return {
    x: Math.min(Math.max(pos.x, 0), maxX),
    y: Math.min(Math.max(pos.y, 0), maxY),
  };
}

function defaultPosition(): Position {
  if (typeof window === 'undefined') return { x: 100, y: 100 };
  return { x: Math.max(20, window.innerWidth - 400), y: 100 };
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export function NotepadFloatingModal({ lessonId, open, onOpenChange }: NotepadFloatingModalProps) {
  const [notepad, setNotepad] = useState<Notepad | null>(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

  const [position, setPosition] = useState<Position>(() => loadPosition() ?? defaultPosition());
  const dragStateRef = useRef<{ startX: number; startY: number; offsetX: number; offsetY: number } | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dirtyRef = useRef(false);

  // Carga inicial al abrir.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSaveStatus('idle');
    getOrCreateNotepad(lessonId)
      .then((n) => {
        if (cancelled) return;
        setNotepad(n);
        setTitle(n.title ?? '');
        setContent(n.content ?? '');
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, lessonId]);

  // Persistir posición en localStorage cuando cambia.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(position));
  }, [position]);

  // Re-clamp al cambiar tamaño de ventana para que el modal nunca quede fuera.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => {
      const el = panelRef.current;
      if (!el) return;
      setPosition((p) => clampPosition(p, el.offsetWidth, el.offsetHeight));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Autosave: tras cambios, espera AUTOSAVE_DELAY_MS y manda PATCH.
  const scheduleSave = useCallback(
    (nextTitle: string, nextContent: string) => {
      if (!notepad) return;
      dirtyRef.current = true;
      setSaveStatus('saving');
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        updateNotepad(notepad.id, { title: nextTitle, content: nextContent })
          .then((n) => {
            setNotepad(n);
            dirtyRef.current = false;
            setSaveStatus('saved');
          })
          .catch((e) => {
            setSaveStatus('error');
            setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error');
          });
      }, AUTOSAVE_DELAY_MS);
    },
    [notepad]
  );

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const onTitleChange = (next: string) => {
    setTitle(next);
    scheduleSave(next, content);
  };

  const onContentChange = (next: string) => {
    setContent(next);
    scheduleSave(title, next);
  };

  // Drag por mouse desde el header.
  const onHeaderPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const el = panelRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    dragStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    };
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
  };

  const onHeaderPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragStateRef.current;
    if (!drag) return;
    const el = panelRef.current;
    if (!el) return;
    const nextX = e.clientX - drag.offsetX;
    const nextY = e.clientY - drag.offsetY;
    setPosition(clampPosition({ x: nextX, y: nextY }, el.offsetWidth, el.offsetHeight));
  };

  const onHeaderPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    dragStateRef.current = null;
    try {
      (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  if (!open) return null;

  return (
    <div
      ref={panelRef}
      className={styles.panel}
      style={{ left: `${position.x}px`, top: `${position.y}px` }}
      role="dialog"
      aria-label="Mi cuaderno"
    >
      <div
        className={styles.header}
        onPointerDown={onHeaderPointerDown}
        onPointerMove={onHeaderPointerMove}
        onPointerUp={onHeaderPointerUp}
      >
        <div className={styles.headerLeft}>
          <Icon name="notebook-pen" size={16} />
          <span className={styles.headerTitle}>Mi cuaderno</span>
        </div>
        <button
          type="button"
          className={styles.closeBtn}
          onClick={() => onOpenChange(false)}
          aria-label="Cerrar cuaderno"
        >
          <Icon name="x" size={16} />
        </button>
      </div>

      <div className={styles.body}>
        {loading ? (
          <div className={styles.center}>
            <Spinner size="sm" />
          </div>
        ) : error ? (
          <p className={styles.error}>{error}</p>
        ) : (
          <>
            <input
              type="text"
              className={styles.titleInput}
              placeholder="Título de la nota"
              maxLength={255}
              value={title}
              onChange={(e) => onTitleChange(e.target.value)}
            />
            <textarea
              className={styles.contentArea}
              placeholder="Escribe aquí tus apuntes…"
              value={content}
              onChange={(e) => onContentChange(e.target.value)}
            />
          </>
        )}
      </div>

      <footer className={styles.footer}>
        {saveStatus === 'saving' && (
          <span className={styles.statusSaving}>
            <Spinner size="sm" /> Guardando…
          </span>
        )}
        {saveStatus === 'saved' && <span className={styles.statusSaved}>Guardado</span>}
        {saveStatus === 'error' && <span className={styles.statusError}>Error al guardar</span>}
      </footer>
    </div>
  );
}
